import CampaignActions from './CampaignActions';
import CampaignIdAndArchContainer from './CampaignIdAndArchContainer';
import CampaignName from './CampaignName';
import AccountManager from './AccountManager';
import LongStringWithTooltip from '../../shared/LongStringWithTooltip';
import { dateFormat } from '../../../utils/utils';
import { TableCell } from '@mui/material';
import { Campaign } from '../slice/schemas/schema';
import { isDisabled, status } from '@helpers/campaigns_helper';
import { useGetReportsQuery } from '../slice/legacyAdminApi';
import { skipToken } from '@reduxjs/toolkit/query';
import { filterReportsByCampaign } from '../reports/ReportsData';
import { ReportStatusIcon } from './ReportStatusIcon';
import { AdminReport } from '../slice/schemas/legacyAdminSchema';

export const approvedStatuses: AdminReport['status'][] = [
  'approved',
  'approved_visit_metrics',
];

type CampaignDataProps = {
  campaign: Campaign;
  expanded: boolean;
  toggleExpanded: () => void;
  className?: string;
};

const CampaignData = (props: CampaignDataProps) => {
  const { data: reportsResponse } = useGetReportsQuery(
    props.expanded ? { campaignIds: [props.campaign.id] } : skipToken
  );
  const { campaign, expanded, toggleExpanded } = props;

  const campId = campaign.id;
  const unassigned = 'Unassigned';
  const partner = campaign.partner ?? null;
  const reportId = campaign.customReportId ?? null;
  const reportGroup = campaign.reportGroup;
  const sortedReports = filterReportsByCampaign(
    reportsResponse?.reports ?? [],
    props.campaign
  ).sort((a, b) => Date.parse(b.report_date) - Date.parse(a.report_date));
  const latestReportStatus =
    (sortedReports[0]
      ? sortedReports[0].status
      : campaign.latestReportStatus) ?? 'pending';

  const campaignNameDisabled =
    sortedReports.length > 0
      ? !sortedReports.some(report => approvedStatuses.includes(report.status))
      : isDisabled(status(campaign.startDate, campaign.measurementStatus));

  return (
    <>
      <TableCell>
        <CampaignActions
          campaign={campaign}
          expanded={expanded}
          toggleExpanded={toggleExpanded}
          partner={partner}
          reportId={reportId}
          reportGroup={reportGroup}
        />
      </TableCell>

      <TableCell>
        <CampaignIdAndArchContainer
          campId={campId}
          archived={campaign.archived}
          partner={partner}
          report_id={reportId}
        />
      </TableCell>

      <TableCell>
        <ReportStatusIcon latestReportStatus={latestReportStatus} />
      </TableCell>

      <TableCell>
        <CampaignName
          campId={campId}
          disabled={campaignNameDisabled}
          campName={campaign.campaignName}
          partner={partner}
          report_id={reportId}
          report_group={reportGroup}
        />
      </TableCell>

      <TableCell className="agency">
        <LongStringWithTooltip string={campaign.advertiser} />
      </TableCell>

      <TableCell className="partner">
        <LongStringWithTooltip string={partner ?? ''} />
      </TableCell>

      <TableCell className="agency-team">
        <LongStringWithTooltip string={campaign.advertiserTeam ?? unassigned} />
      </TableCell>

      <TableCell className="user">
        <AccountManager campaign={campaign} />
      </TableCell>

      <TableCell className="start-date">
        {dateFormat(Date.parse(campaign.startDate))}
      </TableCell>

      <TableCell className="end-date">
        {dateFormat(Date.parse(campaign.endDate))}
      </TableCell>
    </>
  );
};

export default CampaignData;
